"use client"

import { useEffect, useRef } from "react"
import { useInView, useMotionValue, animate } from "framer-motion"
import { Users, Database, Percent, BookCopy } from "lucide-react"

// Reusable animated counter component
function AnimatedCounter({
  value,
  icon,
  label,
}: {
  value: number
  icon: React.ReactNode
  label: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const motionValue = useMotionValue(0)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  useEffect(() => {
    if (isInView) {
      animate(motionValue, value, {
        duration: 2,
        onUpdate: latest => {
          if (ref.current) {
            ref.current.textContent = Math.round(latest).toLocaleString()
          }
        },
      })
    }
  }, [isInView, motionValue, value])

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className="text-4xl text-orange-400 mb-2">{icon}</div>
      <p className="text-4xl md:text-5xl font-extrabold text-white">
        <span ref={ref}>0</span>
        {label.includes("%") ? "%" : "+"}
      </p>
      <p className="text-sm md:text-base text-slate-400 mt-2">
        {label.replace("%", "")}
      </p>
    </div>
  )
}

const stats = [
  {
    value: 50000,
    icon: <Users className="h-10 w-10" />,
    label: "注册用户",
  },
  {
    value: 1000000,
    icon: <Database className="h-10 w-10" />,
    label: "题库题目",
  },
  {
    value: 95,
    icon: <Percent className="h-10 w-10" />,
    label: "通过率%",
  },
  {
    value: 8,
    icon: <BookCopy className="h-10 w-10" />,
    label: "科目覆盖",
  },
]

export const StatisticsSection = () => {
  return (
    <section className="w-full py-20 md:py-28 bg-gradient-to-br from-slate-900 to-gray-900">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map(stat => (
            <AnimatedCounter key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  )
}
